/**
 * PURE academy people selectors (F3-D) — no DB dependency, 100% testable
 * with vitest. Cover: partitioning the directory by tab, filtering by
 * name/national ID/campus, defensive extraction of attendance stats
 * (F3-A columns that may not exist yet), rendering custom fields from the
 * country pack, and picking a ward (guardian).
 */

import type { CountryPack } from "../country/packs";
import type { DirectoryMemberRow, DirectoryRow, OrgIdentity } from "./queries";

/**
 * Joins membership/record rows with the identity map (D3, plan 06).
 *
 * The directory stopped bringing name/email/image in its own query: identity
 * has ONE source (`useOrgIdentities`) and gets attached here, in a pure
 * selector. A member whose identity hasn't loaded yet shows up with all
 * three fields as `null` — the business row still renders and identity
 * hydrates later, instead of the permanent blank name from before.
 *
 * `email: null` can mean "hasn't loaded yet" or "not yours to see": for the
 * UI those are the same case (there's no email to show), and the distinction
 * must not live on the client.
 */
export function hydrateDirectory(
  rows: readonly DirectoryMemberRow[],
  identities: ReadonlyMap<string, OrgIdentity>,
): DirectoryRow[] {
  return attachIdentity(rows, identities, (row) => row.userId);
}

/** The three identity fields attached to a business row. */
export interface IdentityFields {
  name: string | null;
  email: string | null;
  image: string | null;
}

/** A business row already hydrated with identity. */
export type WithIdentity<T> = T & IdentityFields;

/**
 * Attaches identity to ANY business row carrying a `userId` (D3,
 * plan 06 · E3). It's the single implementation of hydration: the
 * directory, the cohort roster, the attendance report and alerts share it
 * instead of repeating four identical `map`s —which is what the four
 * `leftJoin(user, …)` this stage deletes used to be, only in SQL.
 *
 * `userId` is passed as an accessor because PowerSync rows use `user_id` and
 * directory ones use `userId`; the row's shape must not force duplicating
 * the logic.
 */
export function attachIdentity<T>(
  rows: readonly T[],
  identities: ReadonlyMap<string, OrgIdentity>,
  userIdOf: (row: T) => string | null | undefined,
): WithIdentity<T>[] {
  return rows.map((row) => {
    const userId = userIdOf(row);
    const identity = userId ? identities.get(userId) : undefined;
    return {
      ...row,
      name: identity?.name ?? null,
      email: identity?.email ?? null,
      image: identity?.image ?? null,
    };
  });
}

/**
 * Sorts already-hydrated rows by name. Replaces the `ORDER BY user.name` the
 * deleted join used to do: without identity in the query, sorting by name
 * can no longer be decided in SQL. Rows whose identity hasn't arrived yet go
 * to the end (tie-broken by `user_id`) instead of heading the list.
 */
export function sortByIdentityName<T extends IdentityFields>(
  rows: readonly T[],
  fallbackOf: (row: T) => string,
): T[] {
  return rows.toSorted((a, b) => {
    const an = a.name ?? "";
    const bn = b.name ?? "";
    if (an && bn) {
      return an.localeCompare(bn);
    }
    if (an !== bn) {
      // Only one has a name: the one that has it goes first.
      return an ? -1 : 1;
    }
    return fallbackOf(a).localeCompare(fallbackOf(b));
  });
}

/** The four tabs of the people directory. */
export type PeopleTab = "students" | "teachers" | "staff" | "guardians";

/** Maps a `member.role` to the directory tab where it appears. */
export function tabForRole(role: string | null | undefined): PeopleTab {
  switch (role) {
    case "teacher": {
      return "teachers";
    }
    case "owner":
    case "org_admin":
    case "admin":
    case "campus_admin":
    case "staff": {
      return "staff";
    }
    case "guardian": {
      return "guardians";
    }
    // student + legacy (member/viewer/unknown) → students.
    default: {
      return "students";
    }
  }
}

/** Directory partitioned by tab. */
export interface PartitionedDirectory {
  students: DirectoryRow[];
  teachers: DirectoryRow[];
  staff: DirectoryRow[];
  guardians: DirectoryRow[];
}

/** Partitions directory rows by tab according to the membership role. */
export function partitionDirectory(rows: readonly DirectoryRow[]): PartitionedDirectory {
  const out: PartitionedDirectory = { students: [], teachers: [], staff: [], guardians: [] };
  for (const row of rows) {
    out[tabForRole(row.role)].push(row);
  }
  return out;
}

/** Directory filtering options. */
export interface DirectoryFilter {
  /** Free text: matches name, email or national ID (case-insensitive). */
  search?: string;
  /** Campus: matches against the membership's or record's campus. */
  campusId?: string | null;
}

/** Does the row match the search text (name/email/national ID)? */
function matchesSearch(row: DirectoryRow, needle: string): boolean {
  const hay = [row.name, row.email, row.nationalId]
    .filter((v): v is string => typeof v === "string")
    .join(" ")
    .toLowerCase();
  return hay.includes(needle);
}

/** Does the row belong to campus `campusId`? */
function matchesCampus(row: DirectoryRow, campusId: string): boolean {
  return row.studentCampusId === campusId || row.memberCampusId === campusId;
}

/** Applies search + campus filter to a set of rows. */
export function filterDirectory(
  rows: readonly DirectoryRow[],
  filter: DirectoryFilter,
): DirectoryRow[] {
  const needle = filter.search?.trim().toLowerCase() ?? "";
  const campusId = filter.campusId ?? null;
  return rows.filter((row) => {
    if (needle && !matchesSearch(row, needle)) {
      return false;
    }
    if (campusId && !matchesCampus(row, campusId)) {
      return false;
    }
    return true;
  });
}

/** Partitions and filters in one step (what the screen consumes). */
export function buildDirectory(
  rows: readonly DirectoryRow[],
  filter: DirectoryFilter = {},
): PartitionedDirectory {
  return partitionDirectory(filterDirectory(rows, filter));
}

// ─── Attendance (F3-A columns, defensive) ─────────────────────────────────────

/** Attendance stat derived from `cohort_enrollment` (F3-A). */
export interface AttendanceStats {
  present: number | null;
  absent: number | null;
  late: number | null;
  excused: number | null;
  /**
   * Percentage 0–100 if the server derived it, or null. The
   * `attendance_rate` column is a 0..1 fraction and gets scaled here (see
   * {@link attendanceStatsOf}).
   */
  rate: number | null;
  /** Is there at least one piece of attendance data available? */
  hasData: boolean;
}

/** Reads a number from an object trying several candidate keys; null if none. */
function readNum(row: Record<string, unknown>, keys: readonly string[]): number | null {
  for (const k of keys) {
    const v = row[k];
    if (typeof v === "number" && Number.isFinite(v)) {
      return v;
    }
  }
  return null;
}

/**
 * Extracts attendance stats from a raw `cohort_enrollment` row.
 * F3-A adds the columns later; until then it returns `hasData=false`.
 * Tolerates several naming conventions to avoid coupling to the exact schema.
 *
 * `rate` ALWAYS comes out in 0–100 because the three screens that consume it
 * paint it with a `%` after it. The real column the server writes
 * (`cohort_enrollment.attendance_rate`, `use_cases::attendance_stats`) is a
 * 0..1 FRACTION —the same one the gradebook and the certification criterion
 * consume—, so it gets scaled here: without that step, 85% attendance used
 * to show as «0.85 %». The `attendance_percent`/`attendance_pct` aliases
 * already come as a percentage and pass through as-is.
 */
export function attendanceStatsOf(
  raw: Record<string, unknown> | null | undefined,
): AttendanceStats {
  const row = raw ?? {};
  const present = readNum(row, ["attendance_present", "attendance_present_count", "present_count"]);
  const absent = readNum(row, ["attendance_absent", "attendance_absent_count", "absent_count"]);
  const late = readNum(row, ["attendance_late", "attendance_late_count", "late_count"]);
  const excused = readNum(row, ["attendance_excused", "attendance_excused_count", "excused_count"]);
  const fraction = readNum(row, ["attendance_rate"]);
  const percent = readNum(row, ["attendance_percent", "attendance_pct"]);
  const rate = fraction === null ? percent : Math.round(fraction * 1000) / 10;
  const hasData = [present, absent, late, excused, rate].some((v) => v !== null);
  return { present, absent, late, excused, rate, hasData };
}

// ─── Custom fields (country pack) ─────────────────────────────────────────────

/** A record field ready to render (label + value). */
export interface RenderedField {
  key: string;
  label: string;
  value: string;
}

/** Parses `student_profile.custom_fields` (JSON) to a safe flat object. */
export function parseCustomFields(
  raw: string | Record<string, unknown> | null | undefined,
): Record<string, string> {
  if (!raw) {
    return {};
  }
  // E4 (plan 06): the SAME column arrives in two shapes depending on where
  // the row comes from, and both are legitimate. From the local mirror (my
  // own record, via `my_membership_profiles`) it arrives as TEXT, because
  // SQLite has no `jsonb`; from the records REST it already arrives as an
  // object. Normalized here, once, instead of repeating the defensive `JSON.parse` on every screen.
  let parsed: unknown = raw;
  if (typeof raw === "string") {
    try {
      parsed = JSON.parse(raw);
    } catch {
      return {};
    }
  }
  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
    return {};
  }
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(parsed)) {
    if (v == null) {
      continue;
    }
    out[k] = typeof v === "string" ? v : String(v);
  }
  return out;
}

/**
 * Builds the identity CARD rows using the country pack: the national ID
 * carries the local label (`idLabel`), followed by the organization's own
 * `custom_fields`. Only includes fields that have a value.
 */
export function renderIdentityFields(
  pack: CountryPack,
  input: {
    nationalId?: string | null;
    customFields?: string | Record<string, unknown> | null;
  },
): RenderedField[] {
  const out: RenderedField[] = [];
  if (input.nationalId) {
    out.push({ key: "national_id", label: pack.idLabel, value: input.nationalId });
  }
  for (const [key, value] of Object.entries(parseCustomFields(input.customFields))) {
    if (value) {
      out.push({ key, label: key, value });
    }
  }
  return out;
}

// ─── Guardian: ward selection (defensive) ─────────────────────────────────────

/** A minimal guardian link (subset of `guardian_link`). */
export interface WardLink {
  student_user_id: string | null;
  relationship?: string | null;
  is_primary?: number | null;
}

/**
 * Picks a guardian's active ward DEFENSIVELY:
 *   1. the `selectedId` if it still exists among the links,
 *   2. otherwise, the link marked `is_primary`,
 *   3. otherwise, the first one,
 *   4. `undefined` if there are no wards.
 * (The ward's data arrives once the F4 streams land; until then this only
 * resolves the ID to display.)
 */
export function selectWard(
  wards: readonly WardLink[],
  selectedId?: string | null,
): string | undefined {
  const ids = wards
    .map((w) => w.student_user_id)
    .filter((id): id is string => typeof id === "string" && id.length > 0);
  if (ids.length === 0) {
    return undefined;
  }
  if (selectedId && ids.includes(selectedId)) {
    return selectedId;
  }
  const primary = wards.find((w) => w.is_primary === 1 && w.student_user_id);
  return primary?.student_user_id ?? ids[0];
}
